import type { QueryParamsList, QueryParamsRetrieve } from '../query'
import type { ListResponse, Resource, ResourceCreate, ResourceId, ResourceRel, ResourceSort, /* ResourceFilter */ ResourcesConfig, ResourceUpdate, } from '../resource'
import { ApiResource } from '../resource'
import type { CustomerPaymentSource } from './customer_payment_sources'
import type { EventStore } from './event_stores'
import type { Order, OrderType } from './orders'
import type { PaymentGateway } from './payment_gateways'


type ExternalPaymentType = 'external_payments'
type ExternalPaymentRel = ResourceRel & { type: ExternalPaymentType }
type OrderRel = ResourceRel & { type: OrderType }


export type ExternalPaymentSort = Pick<ExternalPayment, 'id'> & ResourceSort
// export type ExternalPaymentFilter = Pick<ExternalPayment, 'id' | 'payment_source_token'> & ResourceFilter


interface ExternalPayment extends Resource {
	
	readonly type: ExternalPaymentType

	/** 
	 * The payment source token, as generated by the external gateway SDK. Credit Card numbers are rejected.
	 * @example ```"xxxx.yyyy.zzzz"```
	 */
	payment_source_token: string
	/** 
	 * External payment options.
	 * @example ```{"foo":"bar"}```
	 */
	options?: Record<string, any> | null
	/** 
	 * Information about the payment instrument used in the transaction.
	 * @example ```{"issuer":"cl bank","card_type":"visa"}```
	 */
	payment_instrument?: Record<string, any> | null

	order?: Order | null
	payment_gateway?: PaymentGateway | null
	wallet?: CustomerPaymentSource | null
	event_stores?: EventStore[] | null

}


interface ExternalPaymentCreate extends ResourceCreate {
	
	/** 
	 * The payment source token, as generated by the external gateway SDK. Credit Card numbers are rejected.
	 * @example ```"xxxx.yyyy.zzzz"```
	 */
	payment_source_token: string
	/** 
	 * External payment options.
	 * @example ```{"foo":"bar"}```
	 */
	options?: Record<string, any> | null

	order: OrderRel

}


interface ExternalPaymentUpdate extends ResourceUpdate {
	
	/** 
	 * External payment options.
	 * @example ```{"foo":"bar"}```
	 */
	options?: Record<string, any> | null

	order?: OrderRel | null

}


class ExternalPayments extends ApiResource<ExternalPayment> {

	static readonly TYPE: ExternalPaymentType = 'external_payments' as const

	async create(resource: ExternalPaymentCreate, params?: QueryParamsRetrieve<ExternalPayment>, options?: ResourcesConfig): Promise<ExternalPayment> {
		return this.resources.create<ExternalPaymentCreate, ExternalPayment>({ ...resource, type: ExternalPayments.TYPE }, params, options)
	}

	async update(resource: ExternalPaymentUpdate, params?: QueryParamsRetrieve<ExternalPayment>, options?: ResourcesConfig): Promise<ExternalPayment> {
		return this.resources.update<ExternalPaymentUpdate, ExternalPayment>({ ...resource, type: ExternalPayments.TYPE }, params, options)
	}

	async delete(id: string | ResourceId, options?: ResourcesConfig): Promise<void> {
		await this.resources.delete((typeof id === 'string')? { id, type: ExternalPayments.TYPE } : id, options)
	}

	async order(externalPaymentId: string | ExternalPayment, params?: QueryParamsRetrieve<Order>, options?: ResourcesConfig): Promise<Order> {
		const _externalPaymentId = (externalPaymentId as ExternalPayment).id || externalPaymentId as string
		return this.resources.fetch<Order>({ type: 'orders' }, `external_payments/${_externalPaymentId}/order`, params, options) as unknown as Order
	}

	async payment_gateway(externalPaymentId: string | ExternalPayment, params?: QueryParamsRetrieve<PaymentGateway>, options?: ResourcesConfig): Promise<PaymentGateway> {
		const _externalPaymentId = (externalPaymentId as ExternalPayment).id || externalPaymentId as string
		return this.resources.fetch<PaymentGateway>({ type: 'payment_gateways' }, `external_payments/${_externalPaymentId}/payment_gateway`, params, options) as unknown as PaymentGateway
	}

	async wallet(externalPaymentId: string | ExternalPayment, params?: QueryParamsRetrieve<CustomerPaymentSource>, options?: ResourcesConfig): Promise<CustomerPaymentSource> {
		const _externalPaymentId = (externalPaymentId as ExternalPayment).id || externalPaymentId as string
		return this.resources.fetch<CustomerPaymentSource>({ type: 'customer_payment_sources' }, `external_payments/${_externalPaymentId}/wallet`, params, options) as unknown as CustomerPaymentSource
	}

	async event_stores(externalPaymentId: string | ExternalPayment, params?: QueryParamsList<EventStore>, options?: ResourcesConfig): Promise<ListResponse<EventStore>> {
		const _externalPaymentId = (externalPaymentId as ExternalPayment).id || externalPaymentId as string
		return this.resources.fetch<EventStore>({ type: 'event_stores' }, `external_payments/${_externalPaymentId}/event_stores`, params, options) as unknown as ListResponse<EventStore>
	}


	isExternalPayment(resource: any): resource is ExternalPayment {
		return resource.type && (resource.type === ExternalPayments.TYPE)
	}


	relationship(id: string | ResourceId | null): ExternalPaymentRel {
		return super.relationshipOneToOne<ExternalPaymentRel>(id)
	}

	relationshipToMany(...ids: string[]): ExternalPaymentRel[] {
		return super.relationshipOneToMany<ExternalPaymentRel>(...ids)
	}


	type(): ExternalPaymentType {
		return ExternalPayments.TYPE
	}

}


const instance = new ExternalPayments()
export default instance

export type { ExternalPayment, ExternalPaymentCreate, ExternalPayments, ExternalPaymentType, ExternalPaymentUpdate }
